/**
 * Helper fn to generate contenful image query strings from required image sizes, for use as background images
 * Use the https://www.contentful.com/developers/docs/references/images-api/ to construct a mobile and desktop image
 *
 * @param {object} obj.image - the image object from contentful
 * @param {string} [obj.alignImage = center] - focus area for cropped images
 * @param {string} [obj.fit = fill] - image resizing behaviour to fit after a crop
 * @param {object[number]} obj.imageSizes - array of one or two sizes (WxH), should have keys that match size string
 * @param {string} obj.size - a string matching one of the imageSizes keys
 *
 * @returns {string} CSS url string for background image usage
 */
const generateQS = ({
  image,
  alignImage = 'center',
  fit = 'fill',
  imageSizes,
  size,
  background,
  format,
}) => {
  // imageSizes is an array of [0] width and/or [1] height, eg [100, 200], [100], [null,200]
  // width and height values are optional provided one is supplied (typically width)
  const widthStr = imageSizes[size][0] ? `&w=${imageSizes[size][0]}` : ``;
  const heightStr = imageSizes[size][1] ? `&h=${imageSizes[size][1]}` : ``;
  const bgFill = background ? `&bg=rgb:${background.slice(1)}` : '';
  const fm = format ? `&fm=${format}` : '&fm=jpg';

  // return the concatenated query string
  return `url(${image?.file?.url}?f=${alignImage}&fit=${fit}${fm}${widthStr}${heightStr}${bgFill})`;
};

export const generateBackgroundImages = (props) =>
  `
  background-image:  ${generateQS({ ...props, fm: 'jpg' })};
  background-image: image-set(
    ${generateQS({ ...props, format: 'avif' })} type("image/avif"), 
    ${generateQS({ ...props, format: 'webp' })} type("image/webp"), 
    ${generateQS({ ...props, format: 'jpg' })} type("image/jpeg")
  )
`;

/*
Example query string generator usage
--------
  const imageSizes = {
    mobile: [608, 342],
    tablet: [959, 306],
  };

  const ExampleQS = styled.div`
    background-size: cover;
    background-image: ${({ image }) => image && generateQS({ image, imageSizes,  size: "mobile" })};

    ${breakpoint.tablet`
      background-image: ${({ image }) => image && generateQS({ image, imageSizes,  size: "tablet" })};
    `};
  `;

--------
End example query string generator usage
*/

export default generateQS;
